import { useCallback, useEffect, useRef, useState } from 'react'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'

import { REDIRECT_DELAY_MS, pageErrorMessageKey } from '../Constants/upgradeErrors'

// Keeps the resolved target in the URL so a reload quotes the same upgrade.
function writeTargetParam(target) {
  const url = new URL(window.location.href)
  url.searchParams.set('target', target)
  window.history.replaceState(null, '', url.toString())
}

// Quote of contract 124g39pcvuz, from the page props; a term change refetches it as JSON.
export default function useUpgradeQuote({
  isEnabled, serverQuote = null, quoteUrl, translate,
}) {
  const { getData, isLoading } = useApiGet()
  const { showError } = useNotifications()
  const [fetchedQuote, setFetchedQuote] = useState(null)
  const isRedirectingRef = useRef(false)
  const serverTarget = isEnabled ? serverQuote?.target : null

  // The BE resolves a missing or blocked ?target= to the first available one; reflect it in the URL.
  useEffect(() => {
    if (!serverTarget) return
    if (new URLSearchParams(window.location.search).get('target') !== serverTarget) writeTargetParam(serverTarget)
  }, [serverTarget])

  const changeTarget = useCallback((target) => {
    if (isRedirectingRef.current) return

    getData({
      path: quoteUrl,
      params: { target },
      isExpectedError: (error) => Boolean(error?.response?.data?.redirectTo),
      onSuccess: ({ data }) => {
        setFetchedQuote(data)
        writeTargetParam(data.target)
      },
      onError: (error) => {
        const { code, redirectTo } = error?.response?.data || {}
        if (redirectTo) {
          isRedirectingRef.current = true
          showError(translate(pageErrorMessageKey(code)))
          setTimeout(() => window.location.assign(redirectTo), REDIRECT_DELAY_MS)
          return
        }
        showError(translate('subscription_checkout.intent_update_error'))
      },
    })
  }, [quoteUrl, getData, showError, translate])

  if (!isEnabled || !serverQuote) return { quote: null, isQuoteLoading: false, changeTarget }

  return { quote: fetchedQuote || serverQuote, isQuoteLoading: isLoading, changeTarget }
}
